// Skeleton loading placeholders for partner portal (inline CSS, no Tailwind).
// Variants: line, card, row (table row), stat (dashboard card).
// Uses CSS keyframe animation for pulse effect.

import { useEffect, useState, type CSSProperties } from 'react';
import { colors } from './styles';

/* ── Keyframes (injected once) ─────────────────────── */

const STYLE_ID = 'ailys-skeleton-keyframes';

function ensureKeyframes() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes ailys-skeleton-pulse {
      0%, 100% { opacity: 0.4; }
      50% { opacity: 0.8; }
    }
  `;
  document.head.appendChild(style);
}

/* ── Delay hook: only show if load takes > 200ms ──── */

function useDelayedVisible(delayMs = 200): boolean {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), delayMs);
    return () => clearTimeout(timer);
  }, [delayMs]);
  return visible;
}

/* ── Base skeleton block ───────────────────────────── */

const baseStyle: CSSProperties = {
  background: 'rgba(148, 163, 184, 0.1)',
  borderRadius: 6,
  animation: 'ailys-skeleton-pulse 1.5s ease-in-out infinite',
};

interface SkeletonProps {
  width?: string | number;
  height?: string | number;
  borderRadius?: number;
  style?: CSSProperties;
}

export function Skeleton({ width = '100%', height = 16, borderRadius = 6, style }: SkeletonProps) {
  const visible = useDelayedVisible();
  useEffect(() => { ensureKeyframes(); }, []);

  if (!visible) return null;

  return (
    <div
      style={{
        ...baseStyle,
        width,
        height,
        borderRadius,
        ...style,
      }}
    />
  );
}

/* ── Skeleton card (dashboard KPI card) ────────────── */

export function SkeletonCard() {
  const visible = useDelayedVisible();
  useEffect(() => { ensureKeyframes(); }, []);

  if (!visible) return null;

  return (
    <div
      style={{
        padding: 20,
        borderRadius: 12,
        background: colors.cardBg,
        border: `1px solid ${colors.border}`,
      }}
    >
      <div style={{ ...baseStyle, width: 80, height: 10, marginBottom: 12 }} />
      <div style={{ ...baseStyle, width: 120, height: 28, marginBottom: 8 }} />
      <div style={{ ...baseStyle, width: '90%', height: 12 }} />
    </div>
  );
}

/* ── Skeleton table row ────────────────────────────── */

export function SkeletonRow({ columns = 5 }: { columns?: number }) {
  const visible = useDelayedVisible();
  useEffect(() => { ensureKeyframes(); }, []);

  if (!visible) return null;

  return (
    <div
      style={{
        display: 'flex',
        gap: 16,
        padding: '12px 0',
        borderBottom: `1px solid ${colors.borderSubtle}`,
      }}
    >
      {Array.from({ length: columns }).map((_, i) => (
        <div
          key={i}
          style={{
            ...baseStyle,
            flex: i === 0 ? 0.8 : 1,
            height: 14,
          }}
        />
      ))}
    </div>
  );
}
